import React, { useState } from 'react';
import { toast } from 'react-toastify';
import GenericModal from '../../../components/modal/GenericModal';
import { useManagePostRequest } from '../../../hooks/useManagePostRequest/useManagePostRequest';
import { enviarComunicacionResultadoLoteAPI } from '../../../consumers/backendApisUrls';

// HU-TR-08 CA-04: confirmación del envío masivo. El backend manda un correo
// independiente por candidato (nunca uno colectivo, ver
// ProcesoWriteUseCase.enviarComunicacionResultadoLote) — acá solo se lista a
// quién le va a llegar, para que GDH pueda revisar la selección antes de
// confirmar (mismo espíritu de CA-05 que el envío individual).
//
// onIniciarEnvio/onFinalizarEnvio (28/09/2026): mismo mecanismo que el envío
// individual — le avisan a BandejaPostulantes para deshabilitar los íconos de
// avión de TODA la selección mientras el lote está en vuelo.
const ConfirmarEnvioComunicacionResultadoLote = ({ toggleModal, refreshPage, idsPostulante = [], postulantes = [],
    onIniciarEnvio, onFinalizarEnvio }) => {
    const [managePostRequest] = useManagePostRequest();
    const [isSending, setIsSending] = useState(false);

    const handleConfirmar = async () => {
        if (idsPostulante.length === 0 || isSending) return;
        setIsSending(true);
        onIniciarEnvio && onIniciarEnvio(idsPostulante);
        const toastId = toast.info(`Enviando ${idsPostulante.length} comunicación(es)...`,
            { autoClose: false, closeButton: false });
        try {
            const api = enviarComunicacionResultadoLoteAPI();
            await managePostRequest(api, idsPostulante, async (response) => {
                // AGA-756: el lote responde 200 aunque algún correo no salga; los
                // fallidos ({ idPostulante: motivo }) se muestran con su motivo para
                // que GDH sepa qué corregir (p. ej. registrar la fecha de vigencia).
                const fallidos = Object.entries(response?.data?.fallidos ?? {});
                if (fallidos.length === 0) {
                    toast.update(toastId, {
                        render: 'Comunicaciones procesadas', type: toast.TYPE.SUCCESS, autoClose: 3000,
                    });
                } else {
                    const nombre = (id) => postulantes.find((p) => String(p.idPostulante) === id)?.nombrePostulante
                        ?? `Postulante ${id}`;
                    toast.update(toastId, {
                        render: (
                            <div>
                                {`${response?.data?.enviados?.length ?? 0} enviada(s), ${fallidos.length} no enviada(s):`}
                                <ul className="mb-0 pl-3">
                                    {fallidos.map(([id, motivo]) => <li key={id}>{`${nombre(id)}: ${motivo}`}</li>)}
                                </ul>
                            </div>
                        ),
                        type: toast.TYPE.WARNING, autoClose: false, closeButton: true,
                    });
                }
                if (refreshPage) await refreshPage();
                toggleModal();
            }, false, async () => {
                toast.dismiss(toastId);
            });
        } finally {
            setIsSending(false);
            onFinalizarEnvio && onFinalizarEnvio(idsPostulante);
        }
    };

    return (
        <GenericModal showModal={toggleModal} titulo="Confirmar envío masivo de comunicaciones" width="520px">
            <div className="modal-body">
                <p className="text-muted" style={{ fontSize: 13 }}>
                    Se enviará a cada candidato la comunicación correspondiente a su propio
                    resultado, de forma independiente — ninguno verá a los demás seleccionados.
                    Esta acción no se puede deshacer.
                </p>

                <div className="form-group">
                    <label className="modal-label mb-0">{`Candidatos seleccionados (${postulantes.length})`}</label>
                    <ul className="mb-0" style={{ maxHeight: 220, overflowY: 'auto', paddingLeft: 18 }}>
                        {postulantes.map((p) => (
                            <li key={p.idPostulante}>
                                {p.nombrePostulante} — <span className="text-muted">{p.desEstado}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="d-flex justify-content-end mt-3">
                    <button type="button" className="btn btn-outline-secondary mr-2" disabled={isSending}
                        onClick={toggleModal}>
                        Cancelar
                    </button>
                    <button type="button" className="btn generic-button-buscador generic-button-hover-rojo"
                        disabled={isSending} onClick={handleConfirmar}>
                        {`Enviar (${postulantes.length})`}
                    </button>
                </div>
            </div>
        </GenericModal>
    );
};

export default ConfirmarEnvioComunicacionResultadoLote;
